/**
 * Reveals streamed text at a steady, readable pace instead of in network-sized jumps.
 *
 * `push` is called with the full text received so far; `render` is called with a growing prefix of it every
 * frame. When far behind it catches up quickly (a fraction of the backlog per frame), when close it types a few
 * characters per frame, so bursts from the network never show up as jumps. `drain` resolves once everything
 * pushed has been shown (immediately if the page is in the background, and never later than `maxDrainMs`).
 */
export class Typewriter {
  private target = '';
  private shown = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private waiters: Array<() => void> = [];
  private stopped = false;

  constructor(
    private readonly render: (text: string) => void,
    private readonly options: { frameMs?: number; minStep?: number; catchUp?: number; maxDrainMs?: number } = {}
  ) {}

  push(text: string): void {
    if (this.stopped) return;
    this.target = text;
    if (this.shown > text.length) this.shown = text.length;
    this.schedule();
  }

  /** Resolves when everything pushed so far has been shown. */
  drain(): Promise<void> {
    if (this.stopped || this.shown >= this.target.length) return Promise.resolve();
    if (typeof document !== 'undefined' && document.hidden) {
      this.flush();
      return Promise.resolve();
    }
    return new Promise(resolve => {
      this.waiters.push(resolve);
      setTimeout(() => this.flush(), this.options.maxDrainMs ?? 1500);
    });
  }

  /** Shows everything received so far at once. */
  flush(): void {
    if (this.stopped) return;
    if (this.shown < this.target.length) {
      this.shown = this.target.length;
      this.render(this.target);
    }
    this.release();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.release();
  }

  private schedule(): void {
    if (!this.timer && !this.stopped) this.timer = setTimeout(this.tick, this.options.frameMs ?? 16);
  }

  private tick = (): void => {
    this.timer = null;
    if (this.stopped) return;
    const backlog = this.target.length - this.shown;
    if (backlog <= 0) {
      this.release();
      return;
    }
    const step = Math.max(this.options.minStep ?? 3, Math.ceil(backlog / (this.options.catchUp ?? 6)));
    this.shown = Math.min(this.target.length, this.shown + step);
    this.render(this.target.slice(0, this.shown));
    this.schedule();
  };

  private release(): void {
    if (this.shown < this.target.length && !this.stopped) return;
    const waiters = this.waiters;
    this.waiters = [];
    waiters.forEach(resolve => resolve());
  }
}
